import { BRAND } from "@/brand/brand";
import { ReactNode, useEffect, useId, useState } from "react";
import { wasAborted } from "../utilities/abortedRequests";
import { useModalContext } from "../components/ModalProvider";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { userAPI } from "../UserAPI";
import dayjs from "dayjs";
import Section from "../components/Section";
import { useUrlPageState } from "../utilities/dataTablePagination";

import McpConnectModal from "../components/McpConnectModal";
import ToolConfigurationDialog from "../components/ToolConfigurationDialog";
import { useRoleAccess } from "../hooks/useRoleAccess";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  PlugZap,
  Settings,
} from "lucide-react";

import { badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Leerzustand der Tabelle: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? (
        <div className="max-w-prose text-sm text-muted-foreground">{hint}</div>
      ) : null}
      {action}
    </div>
  );
}

export default function OrdersPanel() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const ownOrdersPagination = useUrlPageState("ordersPanel_ownOrdersPage");
  const { pushError: _pushError } = useModalContext() as any;
  const fieldId = useId();
  const [consumerOrders, setConsumerOrders] = useState([]) as any;
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [onlyActive, setOnlyActive] = useState(false);
  const [filter, setFilter] = useState("");
  const [showMcpModal, setShowMcpModal] = useState(false);
  const [mcpModalProduct, setMcpModalProduct] = useState<{ id: string; name: string; provider?: string; configurationSchema?: any[] } | null>(null);
  const [mcpBundled, setMcpBundled] = useState(false);
  const [showToolConfigDialog, setShowToolConfigDialog] = useState(false);
  const [toolConfigProduct, setToolConfigProduct] = useState<{ id: string; name: string; configurationSchema?: any[] } | null>(null);
  const roleAccess = useRoleAccess();

  const filterId = `${fieldId}-filter`;
  const onlyActiveId = `${fieldId}-only-active`;

  useEffect(() => {
    // Was dieser Bereich anfragt, endet mit ihm — sonst besetzen seine Anfragen die Verbindungen
    // weiter, während schon der nächste Bereich laden will.
    const abbruch = new AbortController();

    const call = async () => {
      try {
        const orders = await userAPI.getConsumerOrders(authentication.customer, abbruch.signal);
        setConsumerOrders(orders);
      } catch (e: any) {
        // Den Bereich verlassen zu haben ist kein Fehler — und der Ladehinweis bleibt dann stehen,
        // weil ihn ohnehin niemand mehr sieht.
        if (wasAborted(e)) return;

        console.warn("Could not load consumer orders:", e.message);
      } finally {
        setLoadingOrders(false);
      }
    };

    call();

    return () => abbruch.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderColumns: ColumnDef<any>[] = [
    {
      id: "orderDate",
      accessorFn: (order: any) =>
        order.orderDate ? new Date(order.orderDate).getTime() : 0,
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("billingPanel.dateColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {dayjs(row.original.orderDate).format(t("global.dateShort"))}
        </div>
      ),
    },
    {
      id: "provider",
      accessorFn: (order: any) => (order.seller?.name || "").toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("billingPanel.providerColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          <div>{row.original.seller?.name}</div>
          <div>{row.original.seller?.address?.city}</div>
        </div>
      ),
    },
    {
      id: "product",
      accessorFn: (order: any) => {
        const names = (order.orderItems || [])
          .map((orderItem: any) => orderItem.marketplaceOffer?.name)
          .filter((name: string) => name)
          .join(", ");
        return (names || "-").toLowerCase();
      },
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("billingPanel.productColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          {(row.original.orderItems || [])
            .map((orderItem: any) => orderItem.marketplaceOffer?.name)
            .filter((name: string) => name)
            .join(", ") || "-"}
        </div>
      ),
    },
    {
      id: "period",
      accessorFn: (order: any) => {
        const fromDate = order.orderItems?.[0]?.plan?.validFromDate;
        return fromDate ? new Date(fromDate).getTime() : 0;
      },
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("billingPanel.periodColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.orderItems?.[0]?.plan?.validFromDate
            ? dayjs(row.original.orderItems[0].plan.validFromDate).format(t("global.dateShort"))
            : "-"}
          {" - "}
          {row.original.orderItems?.[0]?.plan?.validToDate
            ? dayjs(row.original.orderItems[0].plan.validToDate).format(t("global.dateShort"))
            : "-"}
        </div>
      ),
    },
    {
      id: "pricingModel",
      accessorFn: (order: any) =>
        (order.orderItems?.[0]?.plan?.name || "-").toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("billingPanel.pricingModelColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.orderItems?.[0]?.plan?.name || "-"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const order = row.original;
        const firstItem = order.orderItems?.[0];
        const serviceId = firstItem?.marketplaceOffer?._id || firstItem?.serviceReference || "";
        const productName = (order.orderItems || [])
          .map((item: any) => item.marketplaceOffer?.name)
          .filter((name: string) => name)
          .join(", ") || "Produkt";
        if (!serviceId) return null;
        const offer = firstItem?.marketplaceOffer;
        const hasConfigSchema = (offer?.configurationSchema?.length ?? 0) > 0;
        const openMcpModal = () => {
          setMcpModalProduct({
            id: serviceId,
            name: productName,
            provider: order.seller?.name,
            configurationSchema: offer?.configurationSchema,
          });
          setMcpBundled(false);
          setShowMcpModal(true);
        };
        const openToolConfigDialog = () => {
          setToolConfigProduct({
            id: serviceId,
            name: productName,
            configurationSchema: offer?.configurationSchema,
          });
          setShowToolConfigDialog(true);
        };
        return (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasConfigSchema && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("billingPanel.toolConfig")}
                title={t("billingPanel.toolConfig")}
                onClick={openToolConfigDialog}
              >
                <Settings aria-hidden className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              className="gap-2"
              title={t("billingPanel.mcpConnect")}
              onClick={openMcpModal}
            >
              <PlugZap aria-hidden className="h-4 w-4" />
              {t("billingPanel.mcpConnect")}
            </Button>
          </div>
        );
      },
    },
  ];

  const filterOrders = (orders: any[]) => {
    let filtered = [...orders];

    if (onlyActive) {
      const now = new Date();
      filtered = filtered.filter((order: any) => {
        const fromDate = order.orderItems?.[0]?.plan?.validFromDate;
        const toDate = order.orderItems?.[0]?.plan?.validToDate;
        if (!fromDate || !toDate) return false;
        return new Date(fromDate) <= now && now <= new Date(toDate);
      });
    }

    if (filter.trim()) {
      const searchWords = filter.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter((order: any) => {
        const sellerName = (order.seller?.name || "").toLowerCase();
        const offerNames = (order.orderItems || [])
          .map((item: any) => item.marketplaceOffer?.name || "")
          .join(" ")
          .toLowerCase();
        const planName = (order.orderItems?.[0]?.plan?.name || "").toLowerCase();

        return searchWords.every((word: string) =>
          sellerName.startsWith(word) ||
          offerNames.split(" ").some((offer: string) => offer.startsWith(word)) ||
          planName.startsWith(word)
        );
      });
    }

    return filtered;
  };

  const filteredOrders = filterOrders(consumerOrders);
  const filterActive = onlyActive || filter.trim().length > 0;

  const openBundledMcpModal = () => {
    setMcpModalProduct({ id: "", name: `${BRAND.name} MCP` });
    setMcpBundled(true);
    setShowMcpModal(true);
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {consumerOrders.length > 0 && (
        <button
          type="button"
          onClick={openBundledMcpModal}
          className="flex w-full min-w-0 items-center gap-4 rounded-lg border bg-card p-3 text-left transition-colors animate-in fade-in duration-300 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <PlugZap aria-hidden className="h-5 w-5 shrink-0 text-primary" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">
                {t("billingPanel.bundledMcpTitle")}
              </span>
              {/* span statt <Badge>: der Hinweis liegt in einem <button>, das nur
                  Phrasing-Inhalt aufnehmen darf. */}
              <span className={badgeVariants()}>{t("billingPanel.newBadge")}</span>
            </span>
            <span className="min-w-0 break-words text-sm text-muted-foreground">
              {t("billingPanel.bundledMcpSubtext")}
            </span>
          </span>
          <ChevronRight
            aria-hidden
            className="h-4 w-4 shrink-0 text-muted-foreground"
          />
        </button>
      )}
      <Section title={t("billingPanel.ownOrders")}>
        <div className="mb-4 flex min-w-0 flex-wrap items-end gap-4">
          <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
            <Label htmlFor={filterId}>{t("billingPanel.filter")}</Label>
            <Input
              id={filterId}
              value={filter}
              placeholder={t("billingPanel.filterPlaceholder")}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <div className="flex h-9 items-center gap-2">
            <Checkbox
              id={onlyActiveId}
              checked={onlyActive}
              onCheckedChange={(checked) => setOnlyActive(checked === true)}
            />
            <Label htmlFor={onlyActiveId}>{t("billingPanel.onlyActive")}</Label>
          </div>
        </div>
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={orderColumns}
            data={filteredOrders}
            loading={loadingOrders}
            page={ownOrdersPagination.page}
            onPageChange={ownOrdersPagination.onPageChange}
            previousAriaLabel={t("global.back")}
            nextAriaLabel={t("global.next")}
            emptyMessage={
              filterActive ? (
                <TableEmptyState
                  title={t("billingPanel.noMatchingOrdersTitle")}
                  hint={t("billingPanel.noMatchingOrdersHint")}
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFilter("");
                        setOnlyActive(false);
                      }}
                    >
                      {t("billingPanel.resetFilter")}
                    </Button>
                  }
                />
              ) : (
                <TableEmptyState
                  title={t("billingPanel.noOrdersTitle")}
                  hint={t("billingPanel.noOrdersHint")}
                />
              )
            }
          />
        </div>
      </Section>
      {toolConfigProduct && (
        <ToolConfigurationDialog
          isOpen={showToolConfigDialog}
          onClose={() => {
            setShowToolConfigDialog(false);
            setToolConfigProduct(null);
          }}
          offerId={toolConfigProduct.id}
          productName={toolConfigProduct.name}
          configurationSchema={toolConfigProduct.configurationSchema}
        />
      )}
      {mcpModalProduct && (
        <McpConnectModal
          isOpen={showMcpModal}
          product={mcpModalProduct}
          bundled={mcpBundled}
          onClose={() => {
            setShowMcpModal(false);
            setMcpModalProduct(null);
            setMcpBundled(false);
          }}
          canCreateApiKey={roleAccess.canSeeApiKeys}
        />
      )}
    </div>
  );
}
